<template>
  <div class="study-records">
    <div class="page-header">
      <h2>学习记录管理</h2>
      <p>查看用户学习记录，跟踪学习进度和答题情况</p>
    </div>

    <el-card class="filter-card">
      <el-form :model="filter" inline>
        <el-form-item label="用户筛选">
          <el-select
            v-model="filter.userId"
            placeholder="选择用户"
            clearable
            style="width: 180px;"
          >
            <el-option label="全部用户" :value="null" />
            <el-option
              v-for="user in userOptions"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="视频筛选">
          <el-select
            v-model="filter.videoId"
            placeholder="选择视频"
            clearable
            style="width: 200px;"
          >
            <el-option label="全部视频" :value="null" />
            <el-option
              v-for="video in videoOptions"
              :key="video.id"
              :label="video.title"
              :value="video.id"
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="完成状态">
          <el-select
            v-model="filter.completed"
            placeholder="选择状态"
            clearable
            style="width: 120px;"
          >
            <el-option label="全部" :value="null" />
            <el-option label="已完成" :value="true" />
            <el-option label="未完成" :value="false" />
          </el-select>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            查询
          </el-button>
          <el-button type="info" icon="el-icon-refresh" @click="resetFilter">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card>
      <div class="table-header">
        <div class="table-title">
          <span>学习记录列表</span>
          <span class="total-count">共 {{ totalCount }} 条记录</span>
        </div>
        <div class="export-buttons">
          <el-button type="success" icon="el-icon-download" @click="exportToExcel">
            导出Excel(含图片)
          </el-button>
          <el-button type="primary" icon="el-icon-download" @click="exportToCSV">
            导出CSV
          </el-button>
        </div>
      </div>

      <el-table :data="filteredRecords" border style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="记录ID" width="90" align="center"></el-table-column>
        <el-table-column label="用户信息" width="150">
          <template slot-scope="scope">
            <div class="user-cell">
              <el-avatar :src="getUserAvatar(scope.row.userId)" size="small"></el-avatar>
              <span class="user-name">{{ scope.row.userName }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="videoTitle" label="学习课程" min-width="180"></el-table-column>
        <el-table-column label="学习状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.watched ? 'primary' : 'info'"
              size="mini"
            >
              {{ scope.row.watched ? '已观看' : '未观看' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="完成状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.completed ? 'success' : 'warning'"
              size="mini"
            >
              {{ scope.row.completed ? '已完成' : '未完成' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="答题结果" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.quizCorrect !== null"
              :type="scope.row.quizCorrect ? 'success' : 'danger'"
              size="mini"
            >
              {{ scope.row.quizCorrect ? '正确' : '错误' }}
            </el-tag>
            <span v-else class="no-quiz">未答题</span>
          </template>
        </el-table-column>
        <el-table-column label="观看进度" width="120" align="center">
          <template slot-scope="scope">
            <el-progress
              :percentage="scope.row.progress"
              :stroke-width="6"
              :show-text="false"
            />
            <span class="progress-text">{{ scope.row.progress }}%</span>
          </template>
        </el-table-column>
        <el-table-column prop="watchTime" label="观看时间" width="180"></el-table-column>
        <el-table-column prop="completeTime" label="完成时间" width="180">
          <template slot-scope="scope">
            {{ scope.row.completeTime || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              icon="el-icon-view"
              size="mini"
              @click="handleViewDetail(scope.row)"
            >详情</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 详情对话框 -->
    <el-dialog
      title="学习记录详情"
      :visible.sync="detailDialogVisible"
      width="700px"
    >
      <div v-if="currentRecord" class="record-detail">
        <div class="detail-section">
          <h3>基本信息</h3>
          <el-descriptions :column="2" border size="medium">
            <el-descriptions-item label="记录ID">{{ currentRecord.id }}</el-descriptions-item>
            <el-descriptions-item label="用户">{{ currentRecord.userName }}</el-descriptions-item>
            <el-descriptions-item label="课程">{{ currentRecord.videoTitle }}</el-descriptions-item>
            <el-descriptions-item label="观看时间">{{ currentRecord.watchTime }}</el-descriptions-item>
            <el-descriptions-item label="完成时间">{{ currentRecord.completeTime || '-' }}</el-descriptions-item>
            <el-descriptions-item label="观看进度">
              <el-tag :type="currentRecord.progress === 100 ? 'success' : 'info'">
                {{ currentRecord.progress }}%
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </div>
        
        <div class="detail-section">
          <h3>答题情况</h3>
          <div v-if="currentRecord.quizCorrect !== null && currentQuiz" class="quiz-info">
            <el-descriptions :column="1" border size="medium">
              <el-descriptions-item label="答题结果">
                <el-tag
                  :type="currentRecord.quizCorrect ? 'success' : 'danger'"
                  size="medium"
                >
                  {{ currentRecord.quizCorrect ? '回答正确' : '回答错误' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="题目内容">
                {{ currentQuiz.question }}
              </el-descriptions-item>
              <el-descriptions-item label="选项内容">
                <div v-for="(option, index) in currentQuiz.options" 
                     :key="index" class="option-line">
                  {{ String.fromCharCode(65 + index) }}. {{ option }}
                </div>
              </el-descriptions-item>
              <el-descriptions-item label="正确答案">
                {{ String.fromCharCode(65 + currentQuiz.correctIndex) }}. {{ currentQuiz.options[currentQuiz.correctIndex] }}
              </el-descriptions-item>
            </el-descriptions>
          </div>
          <div v-else class="no-quiz-info">
            <el-alert
              :title="currentRecord.quizCorrect === null ? '用户尚未完成答题' : '暂无关联测验题'"
              type="info"
              :closable="false"
              show-icon
            />
          </div>
        </div>
        
        <div class="detail-section" v-if="currentRecord.signature">
          <h3>签名确认</h3>
          <div class="signature-preview">
            <el-image 
              :src="getFullUrl(currentRecord.signature)" 
              style="max-width: 400px; max-height: 200px;"
              fit="contain"
              :preview-src-list="[getFullUrl(currentRecord.signature)]"
            ></el-image>
          </div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import config from '@/config'
import { api } from '@/services/api'

export default {
  name: 'StudyRecords',
  data() {
    return {
      loading: false,
      detailDialogVisible: false,
      currentRecord: null,
      currentQuiz: null,
      filter: {
        userId: null,
        videoId: null,
        completed: null
      },
      checkinRecords: []
    }
  },
  computed: {
    ...mapState(['studyRecords', 'users', 'videos', 'quizzes']),
    userOptions() {
      return this.users
    },
    videoOptions() {
      return this.videos
    },
    filteredRecords() {
      let records = [...this.studyRecords]
      
      if (this.filter.userId) {
        records = records.filter(record => record.userId === this.filter.userId)
      }
      
      if (this.filter.videoId) {
        records = records.filter(record => record.videoId === this.filter.videoId)
      }
      
      if (this.filter.completed !== null) {
        records = records.filter(record => record.completed === this.filter.completed)
      }
      
      return records.sort((a, b) => {
        if (a.completeTime && b.completeTime) {
          return new Date(b.completeTime) - new Date(a.completeTime)
        }
        if (a.completeTime) return -1
        if (b.completeTime) return 1
        return new Date(b.watchTime) - new Date(a.watchTime)
      })
    },
    totalCount() {
      return this.filteredRecords.length
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    ...mapActions(['fetchStudyRecords', 'fetchUsers', 'fetchVideos', 'fetchQuizzes']),
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.fetchStudyRecords(),
          this.fetchUsers(),
          this.fetchVideos(),
          this.fetchQuizzes()
        ])
        
        const checkinRes = await api.checkin.list()
        if (checkinRes.code === 200) {
          this.checkinRecords = checkinRes.data.list || checkinRes.data || []
        }
      } catch (error) {
        this.$message.error('加载数据失败：' + error.message)
      } finally {
        this.loading = false
      }
    },
    getUserAvatar(userId) {
      const user = this.users.find(u => u.id === userId)
      return user ? user.avatar : 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
    },
    getFullUrl(path) {
      if (!path) return ''
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path
      }
      return config.staticUrl + path
    },
    getUserInfo(userId) {
      return this.users.find(u => u.id === userId) || {}
    },
    getCheckinPhoto(userId, categoryId) {
      const checkin = this.checkinRecords.find(c => 
        c.user_id === userId && c.category_id === categoryId
      )
      return checkin ? this.getFullUrl(checkin.photo_url) : ''
    },
    async handleSearch() {
      await this.loadData()
      this.$message.success('查询完成')
    },
    resetFilter() {
      this.filter = {
        userId: null,
        videoId: null,
        completed: null
      }
      this.$message.info('筛选条件已重置')
    },
    // 导出Excel（HTML格式，支持显示图片）
    exportToExcel() {
      if (this.filteredRecords.length === 0) {
        this.$message.warning('暂无数据可导出')
        return
      }
      
      try {
        let tableRows = ''
        this.filteredRecords.forEach(record => {
          const user = this.getUserInfo(record.userId)
          const categoryId = record.categoryId || record.category_id
          const checkinPhoto = this.getCheckinPhoto(record.userId, categoryId)
          const signature = record.signature ? this.getFullUrl(record.signature) : ''
          const status = record.completed ? '已完成' : '学习中'
          const quizResult = record.quizCorrect === null ? '未完成' : (record.quizCorrect ? '正确' : '错误')
          
          tableRows += `
            <tr>
              <td style="text-align: center; border: 1px solid #000;">${record.userName || ''}</td>
              <td style="text-align: center; border: 1px solid #000;">${user.real_name || ''}</td>
              <td style="text-align: center; border: 1px solid #000;">${user.id_card || ''}</td>
              <td style="text-align: center; border: 1px solid #000; mso-width-source: userset; width: 40pt; height: 40pt;">
                ${checkinPhoto ? `<img src="${checkinPhoto}" width="80" height="80" style="mso-wrap-distance: 0; mso-position-horizontal: center; mso-position-vertical: center;">` : ''}
              </td>
              <td style="text-align: center; border: 1px solid #000; mso-width-source: userset; width: 60pt; height: 30pt;">
                ${signature ? `<img src="${signature}" width="80" height="60" style="mso-wrap-distance: 0; mso-position-horizontal: center; mso-position-vertical: center;">` : ''}
              </td>
              <td style="text-align: center; border: 1px solid #000;">${record.videoTitle}</td>
              <td style="text-align: center; border: 1px solid #000;">${status}</td>
              <td style="text-align: center; border: 1px solid #000;">${quizResult}</td>
              <td style="text-align: center; border: 1px solid #000;">${record.completeTime || '-'}</td>
            </tr>`
        })
        
        const htmlContent = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8">
            <style>
              table { border-collapse: collapse; width: 100%; }
              th { background-color: #4472C4; color: white; font-weight: bold; border: 1px solid #000; padding: 8px; }
              td { border: 1px solid #000; padding: 8px; }
            </style>
          </head>
          <body>
            <table>
              <thead>
                <tr>
                  <th>账号</th>
                  <th>姓名</th>
                  <th>身份证号</th>
                  <th>人像采集</th>
                  <th>签名</th>
                  <th>学习课程</th>
                  <th>状态</th>
                  <th>答题结果</th>
                  <th>完成时间</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </body>
          </html>`
        
        const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        link.setAttribute('href', url)
        link.setAttribute('download', `学习记录_${new Date().toISOString().split('T')[0]}.xls`)
        link.style.visibility = 'hidden'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('导出成功')
      } catch (error) {
        this.$message.error('导出失败: ' + error.message)
      }
    },
    // 导出CSV格式
    exportToCSV() {
      if (this.filteredRecords.length === 0) {
        this.$message.warning('暂无数据可导出')
        return
      }
      
      try {
        const headers = ['账号', '姓名', '身份证号', '人像采集', '签名', '学习课程', '状态', '答题结果', '完成时间']
        const csvContent = [
          headers.join(','),
          ...this.filteredRecords.map(record => {
            const user = this.getUserInfo(record.userId)
            const categoryId = record.categoryId || record.category_id
            const checkinPhoto = this.getCheckinPhoto(record.userId, categoryId)
            const signature = record.signature ? this.getFullUrl(record.signature) : ''
            const status = record.completed ? '已完成' : '学习中'
            const quizResult = record.quizCorrect === null ? '未完成' : (record.quizCorrect ? '正确' : '错误')
            
            return [
              `"${record.userName || ''}"`,
              `"${user.real_name || ''}"`,
              `"${user.id_card || ''}"`,
              `"${checkinPhoto}"`,
              `"${signature}"`,
              `"${record.videoTitle}"`,
              `"${status}"`,
              `"${quizResult}"`,
              `"${record.completeTime || '-'}"`
            ].join(',')
          })
        ].join('\n')
        
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        link.setAttribute('href', url)
        link.setAttribute('download', `学习记录_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('导出成功')
      } catch (error) {
        this.$message.error('导出失败: ' + error.message)
      }
    },
    handleViewDetail(record) {
      this.currentRecord = record
      
      const categoryId = record.categoryId || record.category_id
      const videoId = record.videoId || record.video_id
      
      let quiz = null
      if (categoryId) {
        quiz = this.quizzes.find(q => (q.category_id || q.categoryId) === categoryId)
      }
      if (!quiz && videoId) {
        quiz = this.quizzes.find(q => (q.video_id || q.videoId) === videoId)
      }
      
      this.currentQuiz = quiz
      this.detailDialogVisible = true
    }
  }
}
</script>

<style scoped>
.study-records {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #333;
}

.page-header p {
  margin: 0;
  color: #999;
  font-size: 14px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.total-count {
  margin-left: 12px;
  font-size: 14px;
  color: #666;
  font-weight: normal;
}

.export-buttons {
  display: flex;
  gap: 10px;
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-weight: 500;
}

.no-quiz {
  color: #999;
  font-size: 12px;
}

.progress-text {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.record-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.quiz-info {
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
}

.option-line {
  padding: 4px 0;
  font-size: 13px;
  color: #666;
}

.no-quiz-info {
  padding: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.signature-preview {
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  text-align: center;
}
</style>
